// Per-tab validators for the Crude Registration Create wizard. Each returns an
// array of human-readable error strings (empty = valid), mirroring the Planning
// module's validation.js pattern so the shell can show a single bulleted toast
// and also mark mandatory fields. See rule-crude-registration.md §Create.
const isBlank = v => v === null || v === undefined || String(v).trim() === '';

// Lower-case only the first character so a PascalCase backend key maps to our
// camelCase form-field key (e.g. "AssaYear" -> "assaYear", "CrudeName" ->
// "crudeName"). Nested/collection keys like "Details[0].Sulfur" keep only the
// leaf that matters here.
const toFieldKey = key => {
  if (!key) return key;
  const leaf = key.includes('.') ? key.split('.').pop() : key;
  return leaf.charAt(0).toLowerCase() + leaf.slice(1);
};

// Convert a backend 400 ValidationProblemDetails `errors` map
// ({ "AssaYear": ["'Assa Year' must be between..."] }) into the { field: msg }
// shape the tabs use for inline `invalid` highlighting, plus a flat list of
// messages for a summary toast. Unknown/leftover keys are still surfaced in the
// toast so nothing is silently dropped.
export const mapBackendErrors = (validationErrors, knownFields) => {
  const fields = {};
  const messages = [];
  Object.entries(validationErrors || {}).forEach(([rawKey, msgs]) => {
    const msg = Array.isArray(msgs) ? msgs.join(' ') : String(msgs);
    messages.push(msg);
    const key = toFieldKey(rawKey);
    // Only attach to a field the form actually renders, so an unmatched key
    // doesn't create a phantom highlight; it still shows in the toast list.
    if (!knownFields || knownFields.includes(key)) {
      fields[key] = msg;
    }
  });
  return { fields, messages };
};

// Tab 1 — Crude Info (General + Assay + Yield). Returns { errors, fields } where
// `errors` is the toast list and `fields` maps field-name → message for inline
// `invalid` highlighting.
// `skipGeneral` (Edit Assay flow — CrudeInfoTab renders with hideGeneral there,
// see AssayEditorSection.js) excludes the General-section-only checks (crude
// header required fields + blending composition rows), since those fields
// aren't shown/editable in that context.
export const validateCrudeInfo = (form, { skipGeneral = false } = {}) => {
  const fields = {};
  const required = {
    ...(skipGeneral
      ? {}
      : {
          crudeName: 'Crude Name',
          origin: 'Origin',
          country: 'Country',
          codeG5: 'Code G5',
          productionRate: 'Production Rate (MBCD)',
          penyaluran: 'Penyaluran',
        }),
    assaYear: 'Assay Year',
    lab: 'Lab',
    api: 'API',
    sulfur: 'Sulfur (%-WT)',
    pourPoint: 'Pour Point (°C)',
    gas: 'Gas',
    lNaphta: 'L Naphta',
    hNaphta: 'H Naphta',
    kerosene: 'Kerosene',
    lGasoil: 'L Gasoil',
    hGasoil: 'H Gasoil',
    residu: 'Residu',
  };

  Object.entries(required).forEach(([key, label]) => {
    if (isBlank(form[key])) fields[key] = `${label} is required`;
  });

  // Assay Year must be a valid whole year within 1900..{currentYear+1}
  // (api-crude-registration.md §2 details[].assaYear range). Only checked once a
  // value is present so it doesn't stack on top of the "is required" message.
  if (!isBlank(form.assaYear)) {
    const maxYear = new Date().getFullYear() + 1;
    const year = Number(form.assaYear);
    if (!Number.isInteger(year) || year < 1900 || year > maxYear) {
      fields.assaYear = `Assay Year must be a valid year between 1900 and ${maxYear}`;
    }
  }

  // Blending compositions: each row must have a value in (0, 100]. Skipped
  // along with the rest of General — not shown/editable in that context.
  if (!skipGeneral && form.isBlending) {
    (form.compositions || []).forEach((c, i) => {
      const v = parseFloat(c.composition);
      if (isNaN(v) || v <= 0 || v > 100) {
        fields[`composition_${i}`] =
          `${c.crudeText}: value must be between 0 and 100`;
      }
    });
  }

  const errors = Object.values(fields);

  // Total Yield ≠ 100 is intentionally non-blocking — CrudeInfoTab shows a
  // persistent inline warning, but Save/Next must still proceed.

  return { errors, fields };
};

// Tab 2 — Evaluation. No mandatory fields per the rule (documents optional,
// Notes free text). Kept for a symmetric wizard API.
// eslint-disable-next-line no-unused-vars
export const validateEvaluation = _form => ({ errors: [], fields: {} });

// Tab 3 — Implementation. Each CDU row that is touched must fill its group
// all-or-nothing (technical* group; implementation* group), per the contract.
// Returns { errors, fields } where `errors` is the toast list and `fields` is a
// nested map { [cduId]: { <fieldKey>: true } } marking only the BLANK cells of a
// touched-but-incomplete group, so the tab can highlight exactly which cells the
// user still has to fill.
export const validateImplementation = form => {
  const errors = [];
  const fields = {};
  const impls = form.implementations || {};

  // Mark each blank field of a touched-but-incomplete group on the row's cduId.
  const flagBlanks = (cduId, keys) => {
    keys.forEach(key => {
      if (isBlank(impls[cduId]?.[key])) {
        fields[cduId] = { ...(fields[cduId] || {}), [key]: true };
      }
    });
  };

  Object.entries(impls).forEach(([cduId, row]) => {
    const techKeys = ['technicalStatus', 'technicalRatio', 'technicalLimit'];
    const tech = techKeys.map(k => row[k]);
    const techTouched = tech.some(v => !isBlank(v));
    const techComplete = tech.every(v => !isBlank(v));
    if (techTouched && !techComplete) {
      errors.push(
        `${row.cduName}: Technical Evaluation (Status, Ratio, Limit) must all be filled`
      );
      flagBlanks(cduId, techKeys);
    }

    const plantKeys = [
      'implementationStatus',
      'implementationRatio',
      'implementationLimit',
    ];
    const plant = plantKeys.map(k => row[k]);
    const plantTouched = plant.some(v => !isBlank(v));
    const plantComplete = plant.every(v => !isBlank(v));
    if (plantTouched && !plantComplete) {
      errors.push(
        `${row.cduName}: Plant Test (Status, Ratio, Limit) must all be filled`
      );
      flagBlanks(cduId, plantKeys);
    }
  });

  return { errors, fields };
};
